import * as Sentry from '@sentry/react';

import { apiRequest } from '@/utils/api-request';
import type {
  CollectionShareRole,
  RevokeResponse,
  ShareInviteResponse,
  ShareListResponse,
} from '@/types/share-types';

export type GrantableAccessRole = Exclude<CollectionShareRole, 'owner'>;

export interface ShareAccessRequest {
  emails: string[];
  role?: GrantableAccessRole;
}

interface AccessRecord {
  user_id: string;
  role: string;
  orgid: string;
  granted_by?: string;
  created_at?: string;
}

export interface AccessService {
  share(
    orgId: string,
    resourceId: string,
    body: ShareAccessRequest,
  ): Promise<ShareInviteResponse>;
  listAccess(orgId: string, resourceId: string): Promise<ShareListResponse>;
  updateRole(
    orgId: string,
    resourceId: string,
    email: string,
    role: GrantableAccessRole,
  ): Promise<void>;
  revokeAccess(
    orgId: string,
    resourceId: string,
    email: string,
  ): Promise<RevokeResponse>;
}

export interface AccessServiceOptions {
  pathSegment:
    | 'collection-access'
    | 'context-graph-access'
    | 'conversation-access';
  sentryContext: string;
  notGrantedReason?: string;
}

/**
 * Builds a resource-access service for the platform-api access endpoints
 * (`/{segment}/{orgId}/{resourceId}/access[...]`). Every method throws on
 * failure (after reporting to Sentry) so callers get one error contract;
 * per-email grant outcomes remain data on the share() response.
 */
export function createAccessService({
  pathSegment,
  sentryContext,
  notGrantedReason = 'Failed to grant access',
}: AccessServiceOptions): AccessService {
  const baseUrl = (orgId: string, resourceId: string) =>
    `/${pathSegment}/${orgId}/${resourceId}/access`;

  const report = (error: unknown, action: string): never => {
    Sentry.captureException(error, {
      extra: { context: `${sentryContext}:${action}` },
    });
    throw error;
  };

  return {
    async share(orgId, resourceId, body) {
      try {
        const grantRole = body.role ?? 'viewer';
        const grants = body.emails.map((email) => ({
          user_id: email,
          role: grantRole,
        }));

        const response = await apiRequest<AccessRecord[]>({
          url: `${baseUrl(orgId, resourceId)}/bulk`,
          method: 'POST',
          target: 'platform',
          body: { grants } as unknown as Record<string, unknown>,
        });

        if (response?.detail) {
          throw new Error(response.detail);
        }

        const records = Array.isArray(response.data) ? response.data : [];
        const shared_with = body.emails.map((email) => {
          const record = records.find(
            (r) => r.user_id.toLowerCase() === email.toLowerCase(),
          );
          return {
            email,
            status: record ? ('granted' as const) : ('failed' as const),
            reason: record ? undefined : notGrantedReason,
          };
        });

        return { shared_with, link: '' };
      } catch (error) {
        return report(error, 'share');
      }
    },

    async listAccess(orgId, resourceId) {
      try {
        const response = await apiRequest<AccessRecord[]>({
          url: baseUrl(orgId, resourceId),
          method: 'GET',
          target: 'platform',
        });

        if (response?.detail) {
          throw new Error(response.detail);
        }

        const records = Array.isArray(response.data) ? response.data : [];
        const access = records.map((r) => ({
          email: r.user_id,
          role: r.role as CollectionShareRole,
          granted_by_email: r.granted_by || undefined,
        }));

        return { access };
      } catch (error) {
        return report(error, 'list-access');
      }
    },

    async updateRole(orgId, resourceId, email, role) {
      try {
        const response = await apiRequest({
          url: baseUrl(orgId, resourceId),
          method: 'POST',
          target: 'platform',
          body: { user_id: email, role } as unknown as Record<string, unknown>,
        });

        if (response?.detail || response?.success === false) {
          throw new Error(response?.detail || 'Failed to update role');
        }
      } catch (error) {
        report(error, 'update-role');
      }
    },

    async revokeAccess(orgId, resourceId, email) {
      try {
        const response = await apiRequest({
          url: `${baseUrl(orgId, resourceId)}/${encodeURIComponent(email)}`,
          method: 'DELETE',
          target: 'platform',
        });

        if (response?.detail) {
          throw new Error(response.detail);
        }

        return { email, status: 'revoked' as const };
      } catch (error) {
        return report(error, 'revoke-access');
      }
    },
  };
}
